import React, { FC, useState, useEffect, useRef, useCallback } from 'react'

interface SliderProps {
  value: number,
  min?: number,
  max?: number,
  onChange: (value: number) => void,
  updating: boolean,
}

interface SliderState {
  value: number,
  min: number,
  max: number,
  width: number,
  left: number,
}

const Slider: FC<SliderProps> = ({ value, min, max, onChange, updating }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<SliderState>({
    value: value || max || 100,
    min: min || 0,
    max: max || 100,
    width: 300,
    left: 100,
  })

  const handleResize = useCallback(() => {
    setState({
      ...state,
      width: ref.current?.offsetWidth || 300,
      left: (ref.current?.offsetLeft || 77.5) + 22.5,
    })
  }, [ref, state])

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // don't put handleResize here, it will cause infinite loop

  const moveToClientX = useCallback((x: number) => {
    let newValue = (((x - state.left) / state.width) * (state.max - state.min)) + state.min
    newValue = Math.round(newValue * 10) / 10
    if (newValue < state.min) newValue = state.min
    if (newValue > state.max) newValue = state.max
    setState({ ...state, value: newValue })
    onChange(newValue)
  }, [state, onChange])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    moveToClientX(e.clientX)
  }, [moveToClientX])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    moveToClientX(e.touches[0].clientX)
  }, [moveToClientX])

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', () => {
      document.removeEventListener('mousemove', handleMouseMove)
    })
  }, [handleMouseMove])

  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    document.addEventListener('touchmove', handleTouchMove)
    document.addEventListener('touchend', () => {
      document.removeEventListener('touchmove', handleTouchMove)
    })
  }, [handleTouchMove])

  const _getLeftPercent = () => {
    return ((state.value - state.min) / (state.max - state.min)) * 100;
  }

  const valueDisplay = updating ? (<i className="fa fa-spinner fa-spin"></i>) : value.toFixed(1);
  return (
    <div className="range-slider" onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} ref={ref}>
        <div className="rs-fill"  style={{ width:  _getLeftPercent() + "%"}}></div>
        <div className="rs-handle" style={{ left:  _getLeftPercent() + "%"}}></div>
        <span className="rs-value">{valueDisplay}</span>
    </div>
  );
}

export default Slider